import { DestroyRef, inject, Service } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter, interval, startWith } from 'rxjs';
import { fromEvent } from 'rxjs';
import versionJson from '../../../version.json';
import { DeviceService } from '../device-detector/device.service';
import { onWindowLoad } from '@/helpers/on-window-load.function';

const VERSION_POLL_MS = 2 * 60 * 1000;
export const APP_VERSION_UPDATED_SESSION_KEY = 'kl.appVersionUpdated';

export interface AppVersionPayload {
  version: string;
  builtAtIso?: string;
}

@Service()
export class VersionCheckService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly deviceService = inject(DeviceService);

  private readonly appVersion = (versionJson as AppVersionPayload).version;
  private inFlight = false;

  /** Updated on successful `/version.json` fetch (used by HTTP error interceptor throttle). */
  lastCheckedAt = 0;

  /** Latest `NavigationStart` URL (for ChunkLoadError redirect). */
  lastNavigationTargetUrl: string | null = null;

  constructor() {
    if (this.deviceService.isPlatformServer()) {
      return;
    }

    this.router.events
      .pipe(
        filter((e): e is NavigationStart => e instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (e) => {
          this.lastNavigationTargetUrl = e.url;
        },
      });

    onWindowLoad(() => {
      interval(VERSION_POLL_MS)
        .pipe(
          startWith(0),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe({ next: () => this.checkNow() });

      fromEvent(document, 'visibilitychange')
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            if (document.visibilityState === 'visible') {
              this.checkNow();
            }
          },
        });
    });
  }

  checkNow(): void {
    if (this.deviceService.isPlatformServer() || this.inFlight) {
      return;
    }
    this.inFlight = true;
    void fetch('/version.json', { cache: 'no-store' })
      .then((r) => {
        if (!r.ok) {
          throw new Error(`version.json HTTP ${r.status}`);
        }
        return r.json() as Promise<AppVersionPayload>;
      })
      .then((data) => {
        this.lastCheckedAt = Date.now();
        if (data.version !== this.appVersion) {
          this.triggerReload();
        }
      })
      .catch(() => {
        // keep lastCheckedAt unchanged so error-path throttle can retry
      })
      .finally(() => {
        this.inFlight = false;
      });
  }

  private triggerReload(): void {
    try {
      sessionStorage.setItem(APP_VERSION_UPDATED_SESSION_KEY, '1');
    } catch {
      // ignore
    }
    window.location.reload();
  }
}
